import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { GetCourseLayoutIconFromCode } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { setCourseLayoutHoleCountOnRoundAtom } from "../../../../../../atoms/actions/setCourseLayoutHoleCountOnRoundAtom/setCourseLayoutHoleCountOnRoundAtom";
import { setCourseLayoutIdOnRoundAtom } from "../../../../../../atoms/actions/setCourseLayoutIdOnRoundAtom/setCourseLayoutIdOnRoundAtom";
import { getCourseLayoutIdOnRoundAtom } from "../../../../../../atoms/selectors/getCourseLayoutIdOnRoundAtom/getCourseLayoutIdOnRoundAtom";
import { ConfigureRoundCourseLayoutControl_Booking$key } from "./__generated__/ConfigureRoundCourseLayoutControl_Booking.graphql";

type Props = {
  bookingRef: ConfigureRoundCourseLayoutControl_Booking$key | null;
};

export const ConfigureRoundCourseLayoutControl = ({ bookingRef }: Props) => {
  const booking = useFragment(
    ConfigureRoundCourseLayoutControlFragmentString,
    bookingRef
  );
  const selectedCourseLayoutIdOnRound = useAtomValue(
    getCourseLayoutIdOnRoundAtom
  );
  const setCourseLayoutIdOnRound = useSetAtom(setCourseLayoutIdOnRoundAtom);
  const setCourseLayoutHoleCountOnRound = useSetAtom(
    setCourseLayoutHoleCountOnRoundAtom
  );
  const permittedCourseLayouts =
    booking?.startSlot?.permittedCourseLayouts?.edges?.map((e) => e?.node);
  const dropdownOptions: DropdownSelectOption<string>[] =
    permittedCourseLayouts?.flatMap((courseLayout) => {
      if (!courseLayout?.id) return [];
      const icon = GetCourseLayoutIconFromCode(
        courseLayout?.holeCount || courseLayout?.code
      );
      return {
        value: courseLayout.id,
        leftElement: icon ? icon({}) : undefined,
        triggerElement: icon ? icon({}) : undefined,
        label: "",
      };
    }) ?? [];
  return (
    <VStack width={48}>
      <DropdownSelect
        hideOuterMargin
        includeLeftElementInSelectedValue
        hideChevron
        hideLabelInDisplay
        hideSubLabelInDisplay
        contentSizeOverride="regular"
        options={dropdownOptions}
        value={selectedCourseLayoutIdOnRound}
        onChange={(courseLayoutId) => {
          const matchingCourseLayout = permittedCourseLayouts?.find(
            (courseLayout) => courseLayout?.id === courseLayoutId
          );
          if (matchingCourseLayout) {
            setCourseLayoutIdOnRound({
              courseLayoutId: matchingCourseLayout.id,
            });
            setCourseLayoutHoleCountOnRound({
              courseLayoutHoleCount: matchingCourseLayout.holeCount,
            });
          }
        }}
      />
    </VStack>
  );
};

const ConfigureRoundCourseLayoutControlFragmentString = graphql`
  fragment ConfigureRoundCourseLayoutControl_Booking on Booking {
    startSlot {
      ... on TimeSlot {
        permittedCourseLayouts(memberVisible: true) {
          edges {
            node {
              id
              holeCount
              code
            }
          }
        }
      }
    }
  }
`;
